import { Navigate } from "react-router-dom";
import flagsmith from "@flagsmith/flagsmith";
import { FlagsmithProvider, useFlagsmith } from "@flagsmith/flagsmith/react";
import React, {
  ReactElement,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";
import { AuthContext } from "../../providers/AuthProvider";

interface FeatureFlagProviderProps {
  children: ReactElement;
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  const auth = useContext(AuthContext);
  const environmentKey = process.env.REACT_APP_FLAGSMITH_ENVIRONMENT_KEY || "";
  const [ready, setReady] = useState(false);
  const retryCount = useRef(0);

  if (!environmentKey) {
    throw new Error("REACT_APP_FLAGSMITH_ENVIRONMENT_KEY is not set");
  }

  // Satisfies TypeScript — auth.email is guaranteed by the time this provider
  // mounts, as it sits inside AuthProvider which blocks rendering until authenticated.
  if (!auth?.email) {
    return <Navigate replace to="/login" />;
  }

  const { email, employer } = auth;
  const employerId = employer?.id;

  const options = useMemo(
    () => ({
      environmentID: environmentKey,
      identity: email,
      // Per-employer targeting: CS flips an employer by adding its employer_id
      // to a Flagsmith segment. Snake_case matches the trait keys Flagsmith uses.
      // eslint-disable-next-line camelcase
      traits: { email, employer_id: employerId, employer_name: employer?.company_name },
      cacheFlags: true,
      onChange: (_prev: any, params: { isFromServer: boolean }) => {
        if (params.isFromServer) {
          const flagCount = Object.keys(flagsmith.getAllFlags()).length;
          if (flagCount === 0 && retryCount.current < MAX_RETRIES) {
            retryCount.current += 1;
            const delay = RETRY_DELAY_MS * 2 ** (retryCount.current - 1);
            setTimeout(() => flagsmith.getFlags(), delay);
            return;
          }
        }
        setReady(true);
      },
      onError: (err: Error) => {
        console.error("[FeatureFlags] Flagsmith error:", err);
        if (retryCount.current < MAX_RETRIES) {
          retryCount.current += 1;
          const delay = RETRY_DELAY_MS * 2 ** (retryCount.current - 1);
          setTimeout(() => flagsmith.getFlags(), delay);
        } else {
          setReady(true);
        }
      },
    }),
    [email, employerId, environmentKey]
  );

  return (
    <FlagsmithProvider
      flagsmith={flagsmith}
      options={options}
      key={`${email}:${employerId ?? ""}`}
    >
      {ready ? children : <main />}
    </FlagsmithProvider>
  );
};

export default FeatureFlagProvider;

export const useEnabledFeatureFlag = (featureName: string): boolean => {
  const flagsmithInstance = useFlagsmith();
  if (!featureName) return false;
  return flagsmithInstance.hasFeature(featureName);
};
